// HeroSplash - Full-viewport hero with city search for the homepage
import { useEffect, useState } from "react";
import _ from "lodash";
import InputGroupControl from "../Common/InputGroup";
import HowItWorksSection from "./HowItWorksSection";
import LocationSearchDropdown from "../Common/LocationSearchDropdown";
import { capitalizeString } from "../../actions/common";
import { getGeoLocation } from "../../services/commonServices";
import {
  preloadPlaces,
  getAutocompletePredictions,
} from "../../services/placesService";
import {
  formatAddressForDisplay,
  getLocalStorage,
  getUserId,
  removeLocalStorage,
} from "../../helpers/common";
import { BUY } from "../../constants/common";
import { filterCitiesByQuery } from "../../helpers/mlsCityFilter";
import useAvailableCities from "../../hooks/useAvailableCities";

const HeroSplash = ({
  setUserLocation,
  adType,
  onSearchStart,
  mlsRestrictedCities,
}) => {
  const [locationList, setLocationList] = useState([]);
  const [availableMarketResults, setAvailableMarketResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [location, setLocation] = useState("");
  const userLocation = getLocalStorage("userLocation");
  const { featuredCities, filterCities } = useAvailableCities();
  const locationString = getLocalStorage("LocationString");
  const userId = getUserId();

  // Preload Places script on mount so it's ready when user starts typing
  useEffect(() => {
    preloadPlaces();
  }, []);

  useEffect(() => {
    if (locationString != null && userId) {
      const reqPayload = `address=${capitalizeString(
        locationString
      )}&components=country:US`;
      getAddresseDetails({ reqPayload });
    }
  }, [locationString, userId]);

  const handleFocus = () => {
    setIsFocused(true);
    if (featuredCities?.length > 0 && locationList.length === 0) {
      setAvailableMarketResults(featuredCities);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      setAvailableMarketResults([]);
    }, 200);
  };

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "") {
      getAddresseDetails({ reqPayload, inputValue: val.trim() });
      const filtered = filterCities(val.trim());
      setAvailableMarketResults(filtered);
    } else {
      clearAddressList();
      setAvailableMarketResults(featuredCities || []);
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      clearLocationStates();
    }
  };

  const clearLocationStates = () => {
    if (!locationString) {
      removeLocalStorage("searchedLocation");
      setUserLocation(userLocation);
      setLocation("");
    }
  };

  const getAddresseDetails = async ({ reqPayload, inputValue }) => {
    // MLS-restricted tenants: filter against available cities only
    if (mlsRestrictedCities?.length > 0 && inputValue) {
      const matches = filterCitiesByQuery(inputValue, mlsRestrictedCities);
      setLocationList(matches);
      return;
    }

    const startsWithNumber = inputValue && /^\d/.test(inputValue);
    const isZipCode = inputValue && /^\d{3,5}$/.test(inputValue.trim());

    // locationString auto-select uses geocoding (resolves a known location)
    if (!inputValue && locationString) {
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        const fa = _.get(results[0], "formatted_address", "");
        if (fa !== "United States") {
          handleItemSelection(results[0]);
          removeLocalStorage("LocationString");
        }
      }
      return;
    }

    if (isZipCode) {
      // ZIP code: geocoding is best for exact ZIP resolution
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      const filtered = results.filter((r) => {
        const fa = _.get(r, "formatted_address", "");
        return fa !== "United States";
      });
      setLocationList(filtered.slice(0, 5));
      return;
    }

    // Cities: Places with (cities) type ranks major cities correctly
    // Addresses: Places with address type returns street addresses
    const placesType = startsWithNumber ? ["address"] : ["(cities)"];
    const predictions = await getAutocompletePredictions(
      capitalizeString(inputValue),
      {
        types: placesType,
        componentRestrictions: { country: "us" },
      },
    );

    const items = predictions.map((p) => ({
      _isPlacesPrediction: true,
      place_id: p.place_id,
      formatted_address: p.description,
      types: p.types || [],
    }));

    setLocationList(items.slice(0, 5));
  };

  const clearAddressList = () => {
    setLocationList([]);
    clearLocationStates();
  };

  const handleAvailableMarketClick = async (market) => {
    const reqPayload = `address=${encodeURIComponent(market.city + ", " + market.state)}&components=country:US`;
    const geoData = await getGeoLocation(reqPayload);
    const results = _.get(geoData, "results", []);
    if (results?.length > 0) {
      handleItemSelection(results[0]);
    }
  };

  // Handle clicking a dropdown item - geocoding result or Places prediction.
  // For Places predictions, geocode the description to get full county info
  // (Places getDetails for cities often omits administrative_area_level_2).
  const handleDropdownClick = async (item) => {
    if (item._isMlsCity) {
      // MLS city - geocode to get lat/lng then pass directly
      const reqPayload = `address=${encodeURIComponent(item.formatted_address)}&components=country:US`;
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        handleItemSelection(results[0]);
      }
    } else if (item._isPlacesPrediction) {
      const reqPayload = `address=${encodeURIComponent(item.formatted_address)}&components=country:US`;
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        handleItemSelection(results[0]);
      }
    } else {
      handleItemSelection(item);
    }
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");

    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }

    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }

    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });
    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }

    setLocation(
      city != undefined ? city + ", " + state : formatted_address
    );
    setLocationList([]);

    const locationObject = {
      latitude: _.get(locBounds, "lat", ""),
      longitude: _.get(locBounds, "lng", ""),
      city: city,
      state: state,
      formatted_address:
        city != undefined ? city + ", " + state : formatted_address,
    };

    // Trigger the transition overlay - Home.js handles the delayed location check
    onSearchStart(locationObject, city, state, county, formatted_address);
  };

  return (
    <div className="hero-splash">
      <div className="hero-splash__aurora" />
      <div className="hero-splash__bg">
        <div className="hero-splash__content">
          <h1>
            See what buyers are looking for near you.
          </h1>
          <div className="hero-splash__search">
            <div className="search_field_input input-group position-relative">
              <InputGroupControl
                inputGroupClass="form-control pr-0"
                ariaLabel="Search location"
                ariaDescribedBy="hero-search"
                placeholder={mlsRestrictedCities?.length > 0 ? "Search available markets" : "Enter your city, ZIP, or address"}
                errorMessage=""
                value={location || ""}
                iconClass="search-icon"
                isIconEnable={true}
                iconId="hero-search-btn"
                onChange={(e) => handleLocationChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              {isFocused && (availableMarketResults.length > 0 || locationList.length > 0) && (
                <LocationSearchDropdown
                  availableMarkets={availableMarketResults}
                  placesResults={locationList}
                  onAvailableMarketClick={handleAvailableMarketClick}
                  onPlacesResultClick={handleDropdownClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <HowItWorksSection />
    </div>
  );
};

export default HeroSplash;
